import { ClickOutside } from "@dropbox/dig-components/click_outside";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_DashSearchBar } from "@mirage/analytics/events/types/click_dash_search_bar";
import { PAP_Close_DashSearchBar } from "@mirage/analytics/events/types/close_dash_search_bar";
import { useIsStartPageAugustRevisionEnabled } from "@mirage/august-revision-hook";
import { ChatEntryPoint } from "@mirage/conversations/types";
import { DebugCard } from "@mirage/mosaics/DebugCard";
import {
  focusSearchInput,
  launchSelectedMainContentItem,
} from "@mirage/mosaics/GlobalNav/KeyboardNavigation";
import { useKeyboardNavigation } from "@mirage/mosaics/GlobalNav/useKeyboardNavigation";
import { ResultDropdown } from "@mirage/mosaics/Omnibox/components/ResultDropdown/ResultDropdown";
import { generateSearchURL } from "@mirage/search/hooks/useQueryParams";
import { useAccountIsDropboxer } from "@mirage/service-auth/useDropboxAccount";
import { useEnvironmentContext } from "@mirage/service-environment-context/environmentProvider";
import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import { useDualMode } from "@mirage/service-experimentation/useDualMode";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { publishEvent, SurveyEvent } from "@mirage/service-feedback";
import { namespace } from "@mirage/service-operational-metrics";
import { removePreviousQueryFromCache } from "@mirage/service-typeahead-search";
import type { ScoredUserQuery } from "@mirage/service-typeahead-search/service/types";
import { typeahead } from "@mirage/service-typeahead-search/service/types";
import { OpenTypeaheadAtom } from "@mirage/shared/atoms/typeaheadAtom";
import useAMISSearchResultLogging from "@mirage/shared/hooks/useAMISSearchResultLogging";
import { mouseActivityAtom } from "@mirage/shared/hooks/useInitDetectMouseActivity";
import { calculatePapPositions } from "@mirage/shared/hooks/useResultPAPLogger";
import { preventBubble } from "@mirage/shared/hotkeys";
import { registerHotkeys } from "@mirage/shared/hotkeys/registerHotkeys";
import {
  captureTypeaheadClickMetrics,
  captureTypeaheadImpressionMetrics,
} from "@mirage/shared/operational-metrics/operational-metrics";
import { useIsMobileSizeForSidebar } from "@mirage/shared/responsive/mobile";
import type { SearchFilter } from "@mirage/shared/search/search-filters";
import {
  SearchFilterType,
  syncFiltersWithQuery,
} from "@mirage/shared/search/search-filters";
import { KeyCodes } from "@mirage/shared/util/constants";
import { useShowStackChooserModal } from "@mirage/stacks/StackChooser/StackChooser";
import classNames from "classnames";
import { useAtom, useAtomValue } from "jotai";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useFlyoutPanel } from "../FlyoutPanel/useFlyoutPanel";
import { globalNavIsCollapsedAtom } from "../GlobalNav/atoms";
import { typeaheadIsOpenAtom } from "../SearchBarWithTypeahead/atoms";
import { getAddToStackParams } from "../SearchBarWithTypeahead/util/util";
import { SummaryQnAEnum } from "../SummaryQnaPanel/utils";
import { AdapterInput } from "./components/Omnibar/InputRow/AdapterInput";
import { Omnibar } from "./components/Omnibar/Omnibar";
import type { OmniboxTypeaheadResult } from "./components/ResultRow/adapter/useConvertToOmniboxResults";
import { useConvertToOmniboxResults } from "./components/ResultRow/adapter/useConvertToOmniboxResults";
import { OmniboxResultRowProvider } from "./context/OmniboxResultRowContext";
import styles from "./Omnibox.module.css";

import type { stacks } from "@dropbox/api-v2-client";
import type { SessionStartReason } from "@mirage/analytics/events/enums/session_start_reason";
import { PAP_Click_OmniboxChip } from "@mirage/analytics/events/types/click_omnibox_chip";
import type { TypeaheadResult } from "@mirage/mosaics/SearchBarWithTypeahead/useConvertToTypeaheadResults";
import { useShouldSummarizeNavigateToChat } from "@mirage/service-conversation/hooks/useShouldSummarizeNavigateToChat";

import { SessionManagerName } from "@mirage/analytics/session/session-manager";
import { useRedirectModalState } from "@mirage/eligibility/useRedirectModalState";
import { getCachedConnectorConnections } from "@mirage/service-connectors";
import {
  CtaSource,
  type SummarizeItemParams,
} from "@mirage/service-conversation/router_state_types";
import { buildConversationRouterState } from "@mirage/service-conversation/utils/buildConversationRouterState";
import { isWindowVisible } from "@mirage/service-platform-actions";
import type { TypeaheadSearch } from "@mirage/service-typeahead-search/hooks/useTypeaheadSearch";
import { connectionToSearchFilter } from "@mirage/service-typeahead-search/service/sources/search-filters";
import type { Handler, Handlers } from "@mirage/shared/hotkeys";
import {
  mergePeopleRecommendationIntoFilters,
  replacePartialPeopleFilterText,
} from "@mirage/shared/search/people-recommendations";
import type { Recommendation } from "@mirage/shared/search/recommendation";
import { isDefaultUserQueryResult } from "@mirage/shared/search/user-query";
import { isDefined } from "@mirage/shared/util/tiny-utils";
import { RoutePath } from "@mirage/webapp/routeTypes";
import { ModeAtom } from "./atoms/mode";
import {
  getPapTrackOmniboxFocusMode,
  updateSearchSessionModeAndFeatureLine,
} from "./hooks/useCloseOmniboxSearchSession";
import { useOmniboxMotionFlag } from "./hooks/useOmniboxMotion";
import { useSelectedIndex } from "./hooks/useSelectedIndex";

export type OmniboxProps = TypeaheadSearch & {
  shouldFocusOnRender: boolean;
  initialQuery?: { query: string; inlineCTAText?: string };

  // For omnibox, only the desktop launcher is full width
  fullWidth: boolean;
  augustRevisionEnabled: boolean;
  showBackground: boolean;
  pageScrolled: boolean;
  isOmniboxEnabled: boolean;
  isSlimMode?: boolean;
  customOnBackButtonClicked?: () => void;
};

const metrics = namespace("search-results");

export function Omnibox({
  canShowTypeahead,
  setCanShowTypeahead,
  typeaheadQuery,
  lastEffectiveQuery,
  setTypeaheadQuery,
  typeaheadResults,
  reloadResults,
  papLogResultOpen,
  papLogResultCopy,
  papLogResultShown,
  papLogAddToStack,
  shouldFocusOnRender = false,
  activationType,
  performPreLaunchSearch,
  fullWidth = false,
  augustRevisionEnabled = false,
  showBackground = false,
  filters,
  allFilters,
  isOmniboxEnabled = true,
  isSlimMode = false,
  initialQuery,
  customOnBackButtonClicked,
}: OmniboxProps) {
  const omnibarContainerRef = useRef<HTMLDivElement | null>(null);
  const searchHeaderInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { isDesktop, isDarwin, platform } = useEnvironmentContext();

  // When the mobile left nav is open, keep the typeahead dropdown closed.
  // Otherwise the typeahead dropdown (with a lower z-index) will show on top
  // of the left nav (with a higher z-index).
  const [openedTypeahead, setOpenedTypeahead] = useState(false);
  const [shouldSearchBeBoosted, setShouldSearchBeBoosted] = useState(false);
  const isCollapsed = useAtomValue(globalNavIsCollapsedAtom);
  const [isTypeaheadOpen, setIsTypeaheadOpen] = useAtom(typeaheadIsOpenAtom);
  const isMobileSize = useIsMobileSizeForSidebar(); // for sidebar state only
  const {
    searchSessionManager,
    searchAttemptSessionManager,
    reportSessionAwarePapEvent,
  } = useMirageAnalyticsContext();
  const [openTypeaheadListener, setOpenTypeaheadListener] =
    useAtom(OpenTypeaheadAtom);
  const showStackChooserModal = useShowStackChooserModal(navigate);
  const { redirectModalIsVisibleOrLoading } = useRedirectModalState();
  const { summarize, closeFlyoutPanel } = useFlyoutPanel();
  const [filtersWithParameters, setFiltersWithParameters] = useState<
    SearchFilter[]
  >([]);

  const {
    resetSelectedItem: resetSelectedMainContentItem,
    userHasNotInputKey,
    setUserHasNotInputKey,
  } = useKeyboardNavigation();

  const isAugustStartPageEnabled = useIsStartPageAugustRevisionEnabled();
  const { account, isDropboxer } = useAccountIsDropboxer();
  const [omniboxScopedMode, setOmniboxScopedMode] = useAtom(ModeAtom);
  const { getMotionVersionFor } = useOmniboxMotionFlag();
  const backButtonMotionVersion = getMotionVersionFor("back-button");
  // NOTE: did not refactor this EnvCtx dependency, since currently just adding the environment provider around omnibox, not the whole
  // application yet
  const {
    logTypeaheadResultShown: logAMISResultShown,
    logTypeaheadResultLaunched: logAMISResultLaunched,
  } = useAMISSearchResultLogging(EnvCtx, account, isDropboxer || false);
  const {
    isDualModeEnabled,
    isLauncherSidePanelEnabled,
    isDualModeLauncher,
    getCurrentWindowName,
  } = useDualMode();
  const isUsingTypeaheadFiltersFeatureFlag =
    useFeatureFlagValue("dash_2024_09_30_typeahead_filters") === "ON";
  const shouldSummarizeNavigateToChat = useShouldSummarizeNavigateToChat();

  // ==== BEGIN: new omnibox functionality ====
  const handleBackButtonClick = useCallback(() => {
    if (omniboxScopedMode === typeahead.Mode.Default) return;

    setOmniboxScopedMode(typeahead.Mode.Default);

    if (backButtonMotionVersion === "OFF") {
      setTypeaheadQuery("");
      setCanShowTypeahead(false);
    }

    updateSearchSessionModeAndFeatureLine(
      typeahead.Mode.Default,
      searchSessionManager,
    );
  }, [
    setCanShowTypeahead,
    setOmniboxScopedMode,
    setTypeaheadQuery,
    searchSessionManager,
    omniboxScopedMode,
    backButtonMotionVersion,
  ]);

  const handleScopedButtonClick = useCallback(
    (mode: typeahead.Mode) => {
      setOmniboxScopedMode(mode);
      setCanShowTypeahead(true);
      focusSearchInput();
      reportSessionAwarePapEvent(
        [SessionManagerName.Search],
        (sessionAwareness) => {
          updateSearchSessionModeAndFeatureLine(mode, searchSessionManager);

          return PAP_Click_OmniboxChip({
            actionSurfaceComponent: "omnibox",
            featureLine: "omnibox",
            omniboxFocusMode: mode,
            ...sessionAwareness,
          });
        },
      );
    },
    [
      setOmniboxScopedMode,
      setCanShowTypeahead,
      reportSessionAwarePapEvent,
      searchSessionManager,
    ],
  );

  useEffect(() => {
    const getConnectorDetails = async () => {
      const connections = await getCachedConnectorConnections();

      if (!connections) return;

      const hydratedFilters = (filters || [])
        .map((filter) => {
          if (filter.type !== SearchFilterType.Connector) return filter;

          const connector = connections.find(
            (c) => c.connector?.id_attrs?.type === filter.id,
          );

          if (!connector) return;

          return connectionToSearchFilter(connector);
        })
        .filter(isDefined);

      setFiltersWithParameters(hydratedFilters);
    };

    getConnectorDetails();
  }, [filters]);
  // ==== END: new omnibox functionality ====

  useEffect(() => {
    if (!canShowTypeahead) return;
    setOpenedTypeahead(true);
  }, [canShowTypeahead, setOpenedTypeahead]);

  useEffect(() => {
    if (canShowTypeahead || !openedTypeahead) return;
    reportSessionAwarePapEvent(
      [SessionManagerName.Search],
      (sessionAwareness) =>
        PAP_Close_DashSearchBar({
          actionSurfaceComponent: "omnibox",
          featureLine: "omnibox",
          ...sessionAwareness,
        }),
    );
  }, [canShowTypeahead, openedTypeahead, reportSessionAwarePapEvent]);

  useEffect(() => {
    if (!canShowTypeahead || !isMobileSize || isCollapsed) return;
    setCanShowTypeahead(false);
  }, [canShowTypeahead, isMobileSize, isCollapsed, setCanShowTypeahead]);

  // close flyout panel when typeahead is opened
  useEffect(() => {
    if (!canShowTypeahead) return;
    closeFlyoutPanel();
  }, [canShowTypeahead, closeFlyoutPanel]);

  const extendSearchSession = useCallback(
    (sessionStartReason: SessionStartReason | undefined, query: string) => {
      searchSessionManager.extendOrCreateSession(sessionStartReason);
      if (query === "") return;
      searchSessionManager.updateProperties({
        hasQuery: true,
      });
    },
    [searchSessionManager],
  );

  const handleSubmitToSERP = useCallback(
    (
      query: string,
      result?: typeahead.ScoredResult,
      isDefaultSearch?: boolean,
    ) => {
      setCanShowTypeahead(false);
      searchAttemptSessionManager.updateProperties({
        searchSurface: "top_nav",
      });

      let nextFilters: SearchFilter[] = [];

      if (isUsingTypeaheadFiltersFeatureFlag) {
        nextFilters = syncFiltersWithQuery(query, nextFilters, allFilters);
      }

      if (result?.type === typeahead.ResultType.PeopleFilter) {
        nextFilters = mergePeopleRecommendationIntoFilters(
          filters,
          result.result,
        );
      }

      let nextQuery = query;

      if (result?.type === typeahead.ResultType.PeopleFilter) {
        nextQuery = replacePartialPeopleFilterText(query, result);
      }

      setTypeaheadQuery(nextQuery, !isDefaultSearch);

      publishEvent(SurveyEvent.CompleteSearchQuery, {}, true);
      navigate(generateSearchURL(nextQuery, nextFilters));
    },
    [
      allFilters,
      filters,
      isUsingTypeaheadFiltersFeatureFlag,
      navigate,
      searchAttemptSessionManager,
      setCanShowTypeahead,
      setTypeaheadQuery,
    ],
  );

  // Launches main search through click on SuggestedQuery or PreviousQuery
  // Also used for cmd+enter on UserQueries to force a search on the query (this is different than pressing enter, which does launchResult)
  const handleTaggedSearchSubmit = useCallback(
    (
      result:
        | typeahead.ScoredPreviousQuery
        | ScoredUserQuery
        | typeahead.ScoredPeopleFilter,
      isDefaultSearch: boolean,
    ) => {
      searchSessionManager.updateProperties({
        actionSurfaceComponent: "search_dropdown",
      });

      let queryToSearch = typeaheadQuery ?? "";

      if (result.type === typeahead.ResultType.PreviousQuery) {
        queryToSearch = result.result.query;
      }

      handleSubmitToSERP(queryToSearch, result, isDefaultSearch);
      extendSearchSession("select_typeahead_result", typeaheadQuery);
      searchHeaderInputRef.current?.focus();
    },
    [
      extendSearchSession,
      handleSubmitToSERP,
      searchSessionManager,
      typeaheadQuery,
    ],
  );

  const handleSearchBarClick = () => {
    reportSessionAwarePapEvent(
      [SessionManagerName.Search],
      (sessionAwareness) =>
        PAP_Click_DashSearchBar({
          actionSurfaceComponent: "omnibox",
          featureLine: "omnibox",
          ...sessionAwareness,
        }),
    );
    openTypeahead(typeaheadQuery);
  };

  const onInteractedWithResult = useCallback(
    (result: typeahead.ScoredResult) => {
      searchSessionManager.updateProperties({
        actionSurfaceComponent: "search_dropdown",
      });
      extendSearchSession("select_typeahead_result", typeaheadQuery);
      if ("id3p" in result.result) logAMISResultLaunched(result.result);
    },
    [
      extendSearchSession,
      logAMISResultLaunched,
      searchSessionManager,
      typeaheadQuery,
    ],
  );

  const papTrackOmniboxFocusMode = getPapTrackOmniboxFocusMode(
    isOmniboxEnabled,
    omniboxScopedMode,
    searchSessionManager,
  );

  /**
   * Typeahead result mgmt
   */

  // Note: this largely mirrors useConvertToTypeaheadResults, but includes
  // changes for mapping to the correct icon for displays
  const convertScoredResultsToOmniboxResults = useConvertToOmniboxResults({
    typeaheadQuery,
    setTypeaheadQuery,
    shouldSearchBeBoosted,
    handleTaggedSearchSubmit,
    removePreviousQuery: (result) =>
      removePreviousQueryFromCache(result).then(() => reloadResults()),
    onInteractedWithResult,
    papLogResultOpen,
    papLogResultCopy,
    allFilters,
    papTrackOmniboxFocusMode,
  });

  // Sort the typeaheadResults array to prioritize items with priority
  const searchResults = convertScoredResultsToOmniboxResults(typeaheadResults);

  const userQueryResultIndex = searchResults.findIndex(
    isDefaultUserQueryResult<OmniboxTypeaheadResult>(omniboxScopedMode),
  );
  const userQueryScoredResult = searchResults[userQueryResultIndex]
    ?.scoredResult as ScoredUserQuery;

  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const { selectedIndex, moveUp, moveDown, setSelectedIndexToUserQueryResult } =
    useSelectedIndex({
      userQueryResultIndex,
      typeaheadQuery,
      searchResults,
      canShowTypeahead,
      typeaheadResults,
    });

  /**
   * Keyboard navigation and handlers
   */
  const isMouseActive = useAtomValue(mouseActivityAtom);

  /**
   * Does a fresh search with the most up to date version of the query, and
   * waits to call the callback until all sources have returned from the search
   */
  const getMostUpToDateTypeaheadResults = useCallback(
    // CAUTION: Returning (TypeaheadResult | undefined)[] because sentry is alerting that there are undefined items in the array for some reason
    // Keep these types as-is until we can figure out why this is happening
    (callback: (typeaheadResults: (TypeaheadResult | undefined)[]) => void) => {
      if (lastEffectiveQuery === typeaheadQuery) {
        callback(searchResults);
        return;
      }

      performPreLaunchSearch(
        typeaheadQuery,
        omniboxScopedMode,
        (finalResults) => {
          const typeaheadResults =
            convertScoredResultsToOmniboxResults(finalResults);
          callback(typeaheadResults);
        },
      );
    },
    [
      convertScoredResultsToOmniboxResults,
      performPreLaunchSearch,
      typeaheadQuery,
      omniboxScopedMode,
      lastEffectiveQuery,
      searchResults,
    ],
  );

  const launchResultKeyboardHandler = useCallback<Handler>(() => {
    if (!canShowTypeahead) return;

    getMostUpToDateTypeaheadResults((upToDateTypeaheadResults) => {
      const result = upToDateTypeaheadResults[selectedIndex];

      result?.launchResult?.("enter");
      setIsTypeaheadOpen(false);

      if (result) {
        captureTypeaheadClickMetrics(metrics, result.type, selectedIndex + 1);
      }
      if (result && !result.keepTypeaheadOpen) {
        setCanShowTypeahead(false);
      }
    });
  }, [
    canShowTypeahead,
    getMostUpToDateTypeaheadResults,
    selectedIndex,
    setIsTypeaheadOpen,
    setCanShowTypeahead,
  ]);

  const summarizeKeyboardHandler = useCallback<Handler>(() => {
    if (!canShowTypeahead) return;

    getMostUpToDateTypeaheadResults(async (upToDateTypeaheadResults) => {
      const result = upToDateTypeaheadResults[selectedIndex];
      if (
        !result ||
        !result.summarizable ||
        result.summarizable !== "yes_summarizable"
      )
        return;

      const { resultPosition, resultPositionNoCta } = calculatePapPositions(
        upToDateTypeaheadResults,
        (r) => r?.uuid === result.uuid,
      );

      if (isDualModeLauncher) {
        // Navigate to the Search Results page inside the Persistent Window
        handleSubmitToSERP(typeaheadQuery);
        await new Promise((r) => setTimeout(r));
      }
      if (
        result.type === typeahead.ResultType.Recommendation &&
        shouldSummarizeNavigateToChat
      ) {
        const recommendation: Recommendation = result.scoredResult
          .result as Recommendation;
        const summarizeItemParams: SummarizeItemParams = {
          chatSources: [{ type: "recommendation", recommendation }],
          scenarioMeta: {
            type: "omnibox_summarize_shortcut",
            query: typeaheadQuery,
            searchSessionId: searchSessionManager.getSessionIdOrUndefined(),
          },
        };

        const routerState = buildConversationRouterState(
          CtaSource.SerpSummarize,
          summarizeItemParams,
        );
        setIsTypeaheadOpen(false);
        navigate(RoutePath.CHAT, { state: routerState });
      } else {
        summarize(
          result,
          SummaryQnAEnum.TYPEAHEAD,
          ChatEntryPoint.search_bar_inline,
          resultPosition,
          resultPositionNoCta,
        );
      }

      if (result) {
        captureTypeaheadClickMetrics(metrics, result.type, selectedIndex + 1);
      }

      setCanShowTypeahead(false);
    });
  }, [
    canShowTypeahead,
    getMostUpToDateTypeaheadResults,
    selectedIndex,
    isDualModeLauncher,
    shouldSummarizeNavigateToChat,
    setCanShowTypeahead,
    handleSubmitToSERP,
    typeaheadQuery,
    searchSessionManager,
    setIsTypeaheadOpen,
    navigate,
    summarize,
  ]);

  /**
   * This callback has to be synchronous, otherwise the action will
   * get blocked by the pop-up blocker in mobile browsers and Safari.
   *
   * Popup blocker logic often blocks popup windows when the popup window
   * is not opened as a direct consequence of a user action(like a click).
   * A callback that happens asynchronously is NOT a direct consequence of a user action -
   * it's sometime later and is not directly connected to that action (as the browser sees it),
   * thus the browser may not allow it.
   */
  const launchResultItemClickHandler = useCallback(
    (result: TypeaheadResult, clickedIdx: number) => {
      if (!canShowTypeahead) return;

      result.launchResult?.("click");

      setIsTypeaheadOpen(false);

      captureTypeaheadClickMetrics(metrics, result.type, clickedIdx + 1);

      setCanShowTypeahead(false);
    },
    [canShowTypeahead, setCanShowTypeahead, setIsTypeaheadOpen],
  );

  const copySelected = useCallback(() => {
    getMostUpToDateTypeaheadResults((upToDateTypeaheadResults) => {
      const selectedResult =
        upToDateTypeaheadResults[
          isMouseActive ? (hoveredIdx ?? selectedIndex) : selectedIndex
        ];
      if (selectedResult?.copyResult) {
        selectedResult.copyResult();
      }
    });
  }, [
    getMostUpToDateTypeaheadResults,
    hoveredIdx,
    isMouseActive,
    selectedIndex,
  ]);

  const addToStack = useCallback(() => {
    getMostUpToDateTypeaheadResults((upToDateTypeaheadResults) => {
      const selectedResult = upToDateTypeaheadResults[selectedIndex];
      if (!selectedResult) return;
      const typeaheadResult = typeaheadResults.find(
        (result) => result.uuid === selectedResult.uuid,
      );
      if (!typeaheadResult) return;

      const params = getAddToStackParams(typeaheadResult);
      if (!params) return;

      showStackChooserModal({
        metadataTitle: params.title,
        url: params.url,
        callback: (stack: stacks.Stack | undefined) => {
          papLogAddToStack(typeaheadResult, stack);
          publishEvent(SurveyEvent.AddStackItem, {}, true);
        },
      });
    });
  }, [
    getMostUpToDateTypeaheadResults,
    selectedIndex,
    typeaheadResults,
    showStackChooserModal,
    papLogAddToStack,
  ]);

  const searchQuery = useCallback<Handler>(() => {
    if (
      !!canShowTypeahead &&
      typeaheadQuery.length > 0 &&
      !!userQueryScoredResult
    ) {
      // set the selected index to the suggested query result so autocomplete reacts appropriately
      // userQueryScoredResult can only be suggestedQuery
      setSelectedIndexToUserQueryResult();
      handleTaggedSearchSubmit(userQueryScoredResult, true);
      papLogResultOpen(userQueryScoredResult, "enter");
    }
  }, [
    canShowTypeahead,
    typeaheadQuery.length,
    userQueryScoredResult,
    setSelectedIndexToUserQueryResult,
    handleTaggedSearchSubmit,
    papLogResultOpen,
  ]);

  const openTypeahead = useCallback(
    (newQuery: string) => {
      setTypeaheadQuery(newQuery);
      setCanShowTypeahead(true);
      extendSearchSession("select_search_input", newQuery);
      searchSessionManager.updateProperties({
        featureLine:
          searchSessionManager.getSession()?.properties.featureLine ??
          "omnibox",
      });
    },
    [
      extendSearchSession,
      searchSessionManager,
      setCanShowTypeahead,
      setTypeaheadQuery,
    ],
  );

  const closeTypeahead = useCallback(() => {
    setCanShowTypeahead(false);
  }, [setCanShowTypeahead]);

  const keyHandlers = useMemo<Handlers<"typeahead">>(
    () => ({
      moveUp,
      moveDown,
      launchResult: launchResultKeyboardHandler,
      summarize: summarizeKeyboardHandler,
      searchQuery,
      clearInput: (e) => {
        if (typeaheadQuery) {
          preventBubble(e);
          openTypeahead("");
        } else if (canShowTypeahead && !isDualModeLauncher) {
          preventBubble(e);
          setCanShowTypeahead(false);
        }
      },
      copySelected,
      addToStack,
    }),
    [
      copySelected,
      canShowTypeahead,
      launchResultKeyboardHandler,
      summarizeKeyboardHandler,
      moveDown,
      moveUp,
      openTypeahead,
      searchQuery,
      setCanShowTypeahead,
      typeaheadQuery,
      addToStack,
      isDualModeLauncher,
    ],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // This enables users to quickly launch the first item after foregrounding app
      if (userHasNotInputKey && event.key === KeyCodes.enter) {
        launchSelectedMainContentItem();
      }
      setUserHasNotInputKey(false);

      // Reset selected item when user starts typing or tabbing
      if (event.key !== KeyCodes.arrowDown) {
        resetSelectedMainContentItem();
      }

      registerHotkeys({
        event,
        keyHandlers,
        hotkeyMapName: "typeahead",
        platform,
        isDesktop,
        isDualModeEnabled,
      });
    },
    [
      keyHandlers,
      resetSelectedMainContentItem,
      setUserHasNotInputKey,
      userHasNotInputKey,
      isDesktop,
      isDualModeEnabled,
      platform,
    ],
  );
  const onTypeaheadResultView = useCallback(
    async (item: TypeaheadResult) => {
      const result = typeaheadResults.find((r) => r.uuid === item.uuid);
      const position =
        typeaheadResults.findIndex((r) => r.uuid === item.uuid) + 1;
      if (!result) return;

      // XXX: shown results are for _all_ typeahead results -- currently filtering
      // on matching locally cached server results and not previous queries, etc.
      if ("id3p" in result.result) logAMISResultShown(result.result);
      if (!papLogResultShown) return;
      papLogResultShown(result);

      // Avoid accidental search sessions while window is not visible
      const isVisible = await isWindowVisible(
        isDesktop ? getCurrentWindowName() : undefined,
      );
      if (isVisible) {
        extendSearchSession("scroll_typeahead", typeaheadQuery);
      }
      captureTypeaheadImpressionMetrics(metrics, item, position);
    },
    [
      isDesktop,
      extendSearchSession,
      getCurrentWindowName,
      logAMISResultShown,
      papLogResultShown,
      typeaheadQuery,
      typeaheadResults,
    ],
  );

  useLayoutEffect(() => {
    if (openTypeaheadListener) {
      openTypeahead("");
      searchHeaderInputRef.current?.focus();
      setOpenTypeaheadListener(false);
    }
  }, [
    openTypeahead,
    openTypeaheadListener,
    setOpenTypeaheadListener,
    setTypeaheadQuery,
  ]);

  useEffect(() => {
    setIsTypeaheadOpen(
      Boolean(canShowTypeahead && (searchResults.length || activationType)),
    );
  }, [
    canShowTypeahead,
    searchResults.length,
    activationType,
    setIsTypeaheadOpen,
  ]);

  useEffect(() => {
    if (initialQuery?.query && !redirectModalIsVisibleOrLoading) {
      openTypeahead(initialQuery.query);
    }
  }, [initialQuery?.query, openTypeahead, redirectModalIsVisibleOrLoading]);

  return (
    // This key handler is not a user-interactable element.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      onKeyDown={handleKeyDown}
      onBlur={() => {
        // close handled by outside click, this closes typeahead when tabbing
        if (isMouseActive) return;
        setCanShowTypeahead(false);
      }}
      className={classNames(styles.searchContainer, {
        [styles.fullWidthSearchbar]: fullWidth,
        [styles.typeaheadOpen]: isTypeaheadOpen,
        [styles.augustRevision]: augustRevisionEnabled,
        [styles.showBackground]: showBackground,
      })}
      data-testid="TopNav-searchBar"
    >
      <ClickOutside
        isActive={isTypeaheadOpen && !fullWidth}
        onClickOutside={() => {
          setCanShowTypeahead(false);
        }}
        isBlock
        isClickThroughPortaled={false}
        shouldPropagateMouseEvents={false}
      >
        {/* NOTE: assumes omnibox is always enabled so lets show this instead of the SearchHeader */}
        <Omnibar
          ref={omnibarContainerRef}
          onAttachButtonClicked={() => {}}
          onScopedButtonClicked={handleScopedButtonClick}
          isTypeaheadOpen={isTypeaheadOpen}
          isSlimMode={isSlimMode}
          fullWidth={fullWidth}
        >
          <AdapterInput
            query={typeaheadQuery}
            onQueryUpdate={openTypeahead}
            inputRef={searchHeaderInputRef}
            typeaheadIsOpen={isTypeaheadOpen}
            onClick={handleSearchBarClick}
            shouldFocusOnRender={shouldFocusOnRender}
            onCloseTypeahead={closeTypeahead}
            onShouldBoostSearchToTop={setShouldSearchBeBoosted}
            fullWidth={fullWidth}
            isDarwin={isDarwin}
            selectedIdx={selectedIndex}
            searchResults={searchResults}
            shouldSearchBeBoosted={shouldSearchBeBoosted}
            filters={filters}
            onBackButtonClicked={
              customOnBackButtonClicked ?? handleBackButtonClick
            }
            isDualModeLauncher={isDualModeLauncher}
            inlineCTAText={initialQuery?.inlineCTAText}
            isSlimMode={isSlimMode}
          />
        </Omnibar>
        <OmniboxResultRowProvider reloadResults={reloadResults}>
          <ResultDropdown
            isTypeaheadOpen={isTypeaheadOpen}
            isDualModeLauncher={isDualModeLauncher}
            anchor={omnibarContainerRef}
            typeaheadQuery={typeaheadQuery}
            filters={filtersWithParameters}
            results={searchResults}
            selectedIdx={selectedIndex}
            setHoveredIdx={setHoveredIdx}
            onResultClicked={launchResultItemClickHandler}
            onListItemView={onTypeaheadResultView}
            isMouseActive={isMouseActive}
            fullWidth={fullWidth}
            isAugustStartPageEnabled={isAugustStartPageEnabled}
            isLauncherSidePanelEnabled={isLauncherSidePanelEnabled}
            isSlimMode={isSlimMode}
            isDarwin={isDarwin}
            isInitialQuery={typeaheadQuery === initialQuery?.query}
          />
        </OmniboxResultRowProvider>
      </ClickOutside>
      {/* Portal the debug card to body so that it shows up over the search results card if it is showing */}
      {isTypeaheadOpen &&
        createPortal(
          <DebugCard typeaheadResults={typeaheadResults} />,
          document.body,
        )}
    </div>
  );
}
